import { Link } from 'react-router-dom'
import { DangerTriangleIcon } from '@solar-icons/react/linear/danger-triangle'
import { RefreshIcon } from '@solar-icons/react/linear/refresh'
import type { ExchangeError as ExchangeErrorModel } from '@/stores/sessionStore'
import { Button } from '@/components/ui'

/**
 * Failure states carry a next action rather than just a message. The three
 * configuration failures this backend actually produces — a disabled test
 * route, a missing bearer token, and an unconfigured LLM — are all fixed in
 * Settings, so they link straight there.
 */
const REMEDIES: Record<string, { hint: string; to?: string; cta?: string }> = {
  not_found: {
    hint: 'The route responded 404. ENABLE_TEST_ROUTE may be false on the server, or the base URL points somewhere else.',
    to: '/settings',
    cta: 'Check settings',
  },
  unauthorized: {
    hint: 'The production route rejected the credentials. Add or refresh the bearer token, or switch to the test route.',
    to: '/settings',
    cta: 'Open settings',
  },
  unavailable: {
    hint: 'The backend is up but a dependency is not configured — usually LLM_API_KEY or LLM_MODEL.',
    to: '/diagnostics',
    cta: 'Run diagnostics',
  },
  network: {
    hint: 'Nothing answered at the configured base URL. Confirm uvicorn is running and the URL is right.',
    to: '/diagnostics',
    cta: 'Run diagnostics',
  },
  timeout: {
    hint: 'The pipeline was still working when the client gave up. Raise the request timeout if this is normal for your data.',
    to: '/settings',
    cta: 'Raise timeout',
  },
  pipeline: {
    hint: 'The query reached the pipeline but failed inside it. Rephrasing more specifically usually helps.',
  },
}

export interface ExchangeErrorProps {
  error: ExchangeErrorModel
  onRetry?: () => void
}

export function ExchangeError({ error, onRetry }: ExchangeErrorProps) {
  const remedy = REMEDIES[error.kind]

  return (
    <div className="rounded-xl border border-danger/30 bg-danger-soft px-4 py-3.5">
      <div className="flex gap-2.5">
        <DangerTriangleIcon size={17} strokeWidth={1.75} className="mt-0.5 shrink-0 text-danger" />
        <div className="min-w-0 flex-1">
          <p className="text-sm leading-relaxed font-medium text-fg">
            {error.message}
          </p>

          {remedy?.hint && (
            <p className="mt-1.5 text-xs leading-relaxed text-muted">
              {remedy.hint}
            </p>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {onRetry && (
              <Button
                variant="secondary"
                size="sm"
                icon={<RefreshIcon size={13} strokeWidth={1.75} />}
                onClick={onRetry}
              >
                Retry
              </Button>
            )}
            {remedy?.to && (
              <Link
                to={remedy.to}
                className="text-xs font-medium text-accent underline-offset-4 hover:underline"
              >
                {remedy.cta}
              </Link>
            )}
            <span className="ml-auto font-mono text-[10.5px] text-subtle">
              {error.code ?? error.kind}
              {error.status ? ` · ${error.status}` : ''}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
